<template>
  <div
    :class="['image-checkbox', { 'image-checkbox--disabled': disabled }]"
    @click="toggle"
  >
    <!--While the new value is being saved, a loader replaces the checkbox icon-->
    <v-progress-circular
      v-if="loading"
      class="image-checkbox-loader"
      indeterminate
      color="#65AD45"
      :size="maxWidth"
      :width="2"
    />
    <image-field
      v-else
      :src="getSrc()"
      :max-height="maxHeight"
      :max-width="maxWidth"
    />

    <div
      class="image-checkbox-label"
    >
      {{ label }}
    </div>

    <!--Info tooltip, shown on icon hover-->
    <v-tooltip
      v-if="hasTooltip"
      v-bind="tooltipProps"
    >
      <template #activator="{ on, attrs }">
        <v-icon
          class="image-checkbox-tooltip-icon"
          :style="tooltipIconStyle"
          v-bind="attrs"
          v-on="on"
          @click.stop
        >
          {{ tooltipIcon }}
        </v-icon>
      </template>
      <component
        :is="tooltipComponent"
        :config="tooltipComponentConfig"
      />
    </v-tooltip>
  </div>
</template>

<script>
import ImageField from './ImageField.vue'
import InfoTooltipContent from '@/global/components/tooltips/InfoTooltipContent.vue'
import { isObject } from '@/global/utils/utils'

const DEFAULT_TOOLTIP_ICON = 'mdi-information-slab-circle'
const DEFAULT_TOOLTIP_COLOR = '#6B7280'
const DEFAULT_TOOLTIP_FONT_SIZE = '16px'

export default {
  name: 'ImageCheckbox',

  components: {
    ImageField
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    maxWidth () {
      return this.config?.maxWidth || 20
    },
    maxHeight () {
      return this.config?.maxHeight || 20
    },
    label () {
      return this.config?.label || ''
    },
    disabled () {
      return !!this.config?.disabled || this.loading
    },

    /**
     * Shows a loader instead of the checkbox icon, i.e. while the new value is
     * being saved. Toggling is disabled while it is set.
     *
     * @return {boolean}
     */
    loading () {
      return !!this.config?.loading
    },

    /**
     * Optional info tooltip config, i.e:
     * { text: 'Some description', icon: 'mdi-information', direction: 'top' }
     *
     * @return {object}
     */
    tooltip () {
      return isObject(this.config?.tooltip) ? this.config.tooltip : {}
    },

    hasTooltip () {
      return !!(this.tooltip.text || this.tooltip.component)
    },

    tooltipText () {
      return this.tooltip.text || ''
    },

    tooltipIcon () {
      return this.tooltip.icon || DEFAULT_TOOLTIP_ICON
    },

    // content of the tooltip - same look as the tooltips used on form fields
    tooltipComponent () {
      return this.tooltip.component || InfoTooltipContent
    },

    tooltipComponentConfig () {
      return this.tooltip.config || { text: this.tooltipText }
    },

    tooltipIconStyle () {
      return isObject(this.tooltip.style)
        ? this.tooltip.style
        : { color: DEFAULT_TOOLTIP_COLOR, fontSize: DEFAULT_TOOLTIP_FONT_SIZE }
    },

    tooltipProps () {
      // everything except our own config keys is forwarded to 'v-tooltip'
      const { text, icon, style, direction, component, config, ...tooltipProps } = this.tooltip

      return {
        // transparent, so the tooltip content component provides its own look
        color: 'transparent',
        top: !direction || direction === 'top',
        left: direction === 'left',
        right: direction === 'right',
        bottom: direction === 'bottom',
        ...tooltipProps
      }
    }
  },

  methods: {
    getSrc () {
      if (this.disabled) {
        return '/img/icons/checkbox-disabled-icon.svg'
      }

      return this.value ? '/img/icons/checkbox-checked-icon.svg' : '/img/icons/checkbox-base-icon.svg'
    },

    toggle () {
      if (this.disabled) {
        return
      }

      const newVal = !this.value
      // For v-model
      this.$emit('input', newVal)
      this.$emit('change', { value: newVal })
    }
  }
}
</script>

<style scoped>
.image-checkbox {
  cursor: pointer;
  display: flex;
  align-items: center;
  /* prevent the label from being selected on fast repeated clicks */
  user-select: none;
}

.image-checkbox--disabled {
  cursor: default;
}

.image-checkbox-loader {
  flex-shrink: 0;
}

.image-checkbox-tooltip-icon {
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 6px;
}

.image-checkbox-label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px !important;
  line-height: 20px !important;
  font-weight: 500 !important;
  letter-spacing: 0;
  margin-left: 8px;
  color: #111827;
}
</style>
